import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, RotateCcw, Search, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { createDevolution } from "@/Api/devolution";
import { getSelectClients } from "@/Api/clients";
import { Mask } from "@/utils/mask";

const devolutionSchema = z.object({
  name: z.string().optional(),
  value: z.number().min(0.01, "Valor deve ser maior que zero"),
  client_id: z.number().min(1, "Cliente é obrigatório"),
  pix_key: z.string().min(1, "Chave PIX é obrigatória"),
});

type DevolutionForm = z.infer<typeof devolutionSchema>;

interface Client {
  id: number;
  name: string;
  email: string;
}

interface AddDevolutionProps {
  updateFront: any;
  preSelectedClientId?: number;
  preSelectedClientName?: string;
  dataApi?: any;
  refreshList?: () => Promise<void>;
}

export function AddDevolution({
  updateFront,
  refreshList,
  preSelectedClientId,
  preSelectedClientName,
}: AddDevolutionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    formData: DevolutionForm | null;
  }>({
    isOpen: false,
    formData: null,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<DevolutionForm>({
    resolver: zodResolver(devolutionSchema),
    defaultValues: {
      client_id: preSelectedClientId || 0,
    },
  });

  useEffect(() => {
    if (preSelectedClientId) {
      setValue("client_id", preSelectedClientId);
    }
  }, [preSelectedClientId, setValue]);

  // Carregar lista inicial quando abrir o modal
  useEffect(() => {
    const loadInitialClients = async () => {
      if (!isOpen || preSelectedClientId) return;

      setIsSearching(true);
      try {
        const response = await getSelectClients();
        if (response.data?.response) {
          setClients(response.data.response);
        }
      } catch (error) {
        console.error("Erro ao carregar clientes:", error);
      } finally {
        setIsSearching(false);
      }
    };

    loadInitialClients();
  }, [isOpen, preSelectedClientId]);

  // Debounce search
  useEffect(() => {
    if (!isOpen || preSelectedClientId) return;

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await getSelectClients(searchTerm ? { name: searchTerm } : undefined);
        if (response.data?.response) {
          setClients(response.data.response);
        }
      } catch (error) {
        console.error("Erro ao buscar clientes:", error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, isOpen, preSelectedClientId]);

  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    setValue("client_id", client.id);
    setSearchTerm("");
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = Number(value.replace(/\D/g, "")) / 100;
    setValue("value", numericValue);
  };

  const onSubmit = (data: DevolutionForm) => {
    // Abrir modal de confirmação com os dados
    setConfirmModal({
      isOpen: true,
      formData: data,
    });
  };

  const handleConfirmSubmit = async () => {
    if (!confirmModal.formData) return;

    setIsLoading(true);
    try {
      const response = await createDevolution(confirmModal.formData);
      if (!response.ok) return;
      toast.success("Devolução criada com sucesso!");

      reset();
      setIsOpen(false);
      setConfirmModal({ isOpen: false, formData: null });

      // Se for chamado da listagem de clientes, refresh a página de clientes
      if (preSelectedClientId && refreshList) {
        await refreshList();
        return;
      }

      // Se for da página de devoluções, atualiza a lista local
      if (!preSelectedClientId) {
        updateFront((prev: any) => ({
          ...prev,
          data: [response.data?.response, ...prev.data],
        }));
      }
    } catch (error) {
      toast.error("Erro ao criar devolução");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      reset();
      setConfirmModal({ isOpen: false, formData: null });
      setSearchTerm("");
      setSelectedClient(null);
      setClients([]);
    }
  };

  const getSelectedClientName = () => {
    if (preSelectedClientName) return preSelectedClientName;
    if (selectedClient) return selectedClient.name;
    return "Cliente não encontrado";
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {preSelectedClientId ? (
          <Button variant="outline" size="icon" className="h-8 w-8">
            <RotateCcw className="h-4 w-4" />
          </Button>
        ) : (
          <Button className="bg-primary hover:bg-primary/90">
            <Plus className="mr-2 h-4 w-4" />
            Nova Devolução
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Nova Devolução</DialogTitle>
          <DialogDescription>
            Crie uma nova solicitação de devolução para o cliente.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="client_id">Cliente</Label>
            {preSelectedClientId && preSelectedClientName ? (
              <Input
                value={preSelectedClientName}
                disabled
                className="bg-muted"
              />
            ) : (
              <div className="space-y-2">
                {selectedClient ? (
                  <div className="flex items-center justify-between p-2 border rounded-md bg-muted">
                    <span>{selectedClient.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedClient(null);
                        setValue("client_id", 0);
                      }}
                      className="h-6 px-2 text-xs"
                    >
                      Alterar
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        placeholder="Buscar cliente pelo nome..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                      {isSearching && (
                        <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                      )}
                    </div>
                    <div className="border rounded-md max-h-40 overflow-auto">
                      {clients.length > 0 ? (
                        clients.map((client) => (
                          <button
                            key={client.id}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 text-sm border-b last:border-b-0"
                            onClick={() => handleSelectClient(client)}
                          >
                            {client.name}
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-sm text-gray-500 text-center">
                          {isSearching ? "Buscando..." : "Nenhum cliente encontrado"}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {errors.client_id && (
              <p className="text-sm text-red-500">{errors.client_id.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Descrição</Label>
            <Textarea
              {...register("name")}
              placeholder="Descreva o motivo da devolução..."
              className="min-h-[80px]"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

           <div className="space-y-2">
            <Label htmlFor="pix_key">Chave PIX</Label>
            <Input
              {...register("pix_key")}
              placeholder="Digite a chave PIX para receber a devolução"
            />
            {errors.pix_key && (
              <p className="text-sm text-red-500">{errors.pix_key.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="value">Valor</Label>
            <Input
              typeMask="currency"
              onChange={handleValueChange}
              placeholder="R$ 0,00"
            />
            {errors.value && (
              <p className="text-sm text-red-500">{errors.value.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-primary hover:bg-primary/90"
            >
              {isLoading ? "Criando..." : "Criar Devolução"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <Dialog 
      open={confirmModal.isOpen} 
      onOpenChange={(open) => setConfirmModal({ isOpen: open, formData: null })}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Confirmar Criação de Devolução</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja criar esta devolução?
          </DialogDescription>
        </DialogHeader>

        {confirmModal.formData && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-2">
                Resumo da Devolução
              </h4>
              <div className="grid grid-cols-1 gap-2 text-sm">
                <div>
                  <span className="font-medium">Cliente:</span>
                  <p>{getSelectedClientName()}</p>
                </div>
                <div>
                  <span className="font-medium">Descrição:</span>
                  <p>{confirmModal.formData.name || "Não informada"}</p>
                </div>
                <div>
                  <span className="font-medium">Valor:</span>
                  <p className="text-lg font-semibold text-green-600">
                    {Mask.formatCurrency(confirmModal.formData.value)}
                  </p>
                </div>
                <div>
                  <span className="font-medium">Chave PIX:</span>
                  <p className="font-mono text-sm break-all">
                    {confirmModal.formData.pix_key}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirmModal({ isOpen: false, formData: null })}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            disabled={isLoading}
            className="bg-primary hover:bg-primary/90"
          >
            {isLoading ? "Criando..." : "Confirmar e Criar Devolução"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
  );
}
